/**
 * What a seat thinks everyone else is holding, updated as they act.
 *
 * A bot without this is playing solitaire: it can compute its equity against a random
 * hand and nothing else, so it folds to every bet from a range that never bluffs and
 * calls every bet from a range that always has it. Hand reading is most of the gap
 * between the tiers, which is why `handReading` scales how hard the narrowing bites
 * rather than switching it on and off — a weak player reads a little and wrongly.
 *
 * The estimate is deliberately crude in one specific way: it narrows by hand STRENGTH
 * order, not by a solved strategy. Real players do not raise the top of their range
 * and fold the rest, and neither will these bots once the policy adds bluffs — but as
 * a model of what an opponent is REPRESENTING, the strength ordering is close enough
 * to price a decision, and it is honest about being a model.
 */
import { Range, comboCode, comboCards } from "../range";
import { boardStrength } from "./strength";
import { handRank } from "./ranking";
import { rangeCodes, openPercent } from "./preflop";
import type { Card } from "../types";
import type { Street } from "../types";

export interface SeatRead {
  /**
   * The sharp read: what this seat's actions imply, narrowed as hard as the evidence
   * allows. Every observer shares it, because the ACTIONS are public and the inference
   * from them is not a matter of opinion — how much of it a given player picks up on
   * is, which is what `perceived` below is for.
   */
  range: Range;
  /** The unnarrowed opening range, kept so a weak observer can be blended back to it. */
  base: Range;
  /** Width when the hand started, for "he opened 22% and is now betting" reasoning. */
  openedPercent: number;
  /** Aggressive actions taken this hand — the crude aggression signal. */
  raises: number;
  calls: number;
  checks: number;
}

/**
 * Long-run observations, which are what let a bot adapt across hands rather than only
 * within one. Kept small on purpose: these are the four numbers that actually change
 * a decision, not a HUD.
 */
export interface SeatHistory {
  hands: number;
  vpip: number;
  pfr: number;
  betsFaced: number;
  foldsToBet: number;
}

export function emptyHistory(): SeatHistory {
  return { hands: 0, vpip: 0, pfr: 0, betsFaced: 0, foldsToBet: 0 };
}

/** Observed fold-to-bet frequency, or null when there is not enough to go on. */
export function foldToBet(h: SeatHistory, minSample = 8): number | null {
  return h.betsFaced >= minSample ? h.foldsToBet / h.betsFaced : null;
}

/** Observed voluntary-entry frequency as a percent, or null when too thin. */
export function observedVpip(h: SeatHistory, minSample = 12): number | null {
  return h.hands >= minSample ? (h.vpip / h.hands) * 100 : null;
}

/**
 * The starting belief about a seat that has just entered the pot.
 *
 * Uses what has actually been observed when there is enough of it, and the positional
 * baseline otherwise — which is the right way round. A bot that assumes every opponent
 * plays the baseline is unbeatable by a nit and free money for a maniac.
 */
export function openingRead(
  seatsBehind: number, raised: boolean, history: SeatHistory, handReading: number
): { range: Range; percent: number } {
  const baseline = raised ? openPercent(seatsBehind) : openPercent(seatsBehind) * 1.6;
  const seen = observedVpip(history);
  /**
   * Blend toward what has been observed in proportion to hand-reading skill. A bot
   * with handReading 0 never updates off observation and plays the baseline forever,
   * which is exactly what a weak player does.
   */
  const percent = seen === null ? baseline
    : baseline + (seen * (raised ? 0.55 : 1) - baseline) * Math.min(1, handReading);

  const earliness = Math.min(1, seatsBehind / 8);
  const codes = rangeCodes({
    percent, earliness, style: { looseness: 1, positionAwareness: Math.min(1, handReading) },
  });
  const w: Record<string, number> = {};
  for (const c of codes) w[c] = 1;
  return { range: Range.fromCodes(w), percent };
}

/**
 * Narrow a range by an action.
 *
 * `strength` is where the action sits on a scale from "gave up" to "committed": a fold
 * is not modelled here (the seat is gone), a check is weak, a call is middling, a raise
 * is strong, and a big raise is stronger. `handReading` scales how far the narrowing
 * goes, so a weak bot ends up with a range that is nearly the one it started with.
 *
 * Postflop the narrowing is by EQUITY on the actual board, not by preflop rank: on
 * 7-6-5 with two hearts, 98s is a monster and AKo is nothing, and a bot that narrows
 * by preflop rank there will confidently misread the entire hand.
 */
export interface NarrowSpec {
  range: Range;
  street: Street;
  board: readonly Card[];
  /** 0 = gave up, 0.5 = continued, 1 = committed. */
  strength: number;
  handReading: number;
  /** Per-combo equity on this board, when the caller has it. Preflop rank otherwise. */
  equityOf?: (comboId: number) => number;
}

export function narrow(spec: NarrowSpec): Range {
  const { range, strength, handReading } = spec;
  const bite = Math.max(0, Math.min(1.4, handReading));
  if (bite === 0) return range;

  /**
   * The keep-fraction: how much of the range survives. A committed action keeps the
   * top fifth, a check keeps almost everything but tilts the weights down.
   */
  const keep = strength >= 0.9 ? 0.22
    : strength >= 0.7 ? 0.42
    : strength >= 0.45 ? 0.70
    : strength >= 0.2 ? 0.95
    : 1.0;

  const ids = range.ids();
  if (ids.length === 0) return range;

  /**
   * Preflop, "strong" means high in the hand ordering. After the flop it means strong
   * ON THIS BOARD, which is a completely different ordering: on 7-6-5 with two hearts,
   * 98s is a monster and AKo is nothing. Narrowing a postflop range by preflop rank is
   * how a bot ends up certain it is beaten by a hand that missed everything.
   */
  const score = spec.equityOf ? (id: number) => spec.equityOf!(id)
    : spec.board.length >= 3
      ? (id: number) => { const [a, b] = comboCards(id); return boardStrength(a, b, spec.board); }
      : (id: number) => 1 - handRank(comboCode(id)) / 168;

  const scored = ids.map((id) => ({ id, s: score(id) })).sort((a, b) => b.s - a.s);

  /**
   * Weights fall off smoothly past the cut rather than dropping to zero. A hard cut
   * makes the bot certain, and a bot that is certain gets stacked by the one hand its
   * model said was impossible. The tail keeps a little weight on everything the
   * opponent could still have, which is also true of real opponents.
   */
  /**
   * At full hand-reading the cut is exactly `keep`. The earlier version divided the
   * bite by 1.4 first, so even a perfect reader only ever narrowed a betting range to
   * 59% instead of 42% — every bot was systematically too optimistic about how much
   * air it was facing, which showed up as bots paying off value bets all night.
   */
  const cut = Math.max(1, Math.floor(scored.length * (keep + (1 - keep) * (1 - Math.min(1, bite)))));
  const out = range.clone();
  for (let i = 0; i < scored.length; i++) {
    const e = scored[i]!;
    if (i < cut) continue;
    const over = (i - cut) / Math.max(1, scored.length - cut);
    // From full weight at the cut down to a floor, so nothing is ever ruled out.
    const factor = Math.max(0.04, 1 - over * bite);
    out.w[e.id] = out.w[e.id]! * factor;
  }
  return out;
}

/** Action strengths, in the units `narrow` expects. Named so the policy reads clearly. */
export const STRENGTH = {
  check: 0.15,
  callSmall: 0.45,
  callBig: 0.62,
  bet: 0.72,
  raise: 0.9,
  reraise: 1.0,
} as const;

/**
 * Where a call sits, by the price. Calling a pot-sized bet says far more than calling
 * a third of the pot, and treating them the same is how a bot ends up thinking a
 * min-click means something.
 */
export function callStrength(betFraction: number): number {
  return betFraction >= 0.7 ? STRENGTH.callBig : STRENGTH.callSmall;
}

/**
 * What a particular player sees.
 *
 * The sharp read is shared, because the actions are public. What differs is how much
 * of it each player actually uses: a good one plays against the narrowed range, a weak
 * one plays against roughly the range the opponent started with, and the players in
 * between see something blurred in proportion.
 *
 * Blending toward the base range rather than recomputing the whole narrowing per
 * observer is the cheap version of the same idea, and it is what makes `handReading`
 * a real dial instead of a number in a table nobody consults. The first version shared
 * ONE read narrowed at whichever profile happened to sit in the first seat, so every
 * bot at the table held the same belief and the dial did nothing at all.
 */
export function perceived(read: SeatRead, handReading: number): Range {
  const w = Math.max(0, Math.min(1, handReading));
  if (w >= 0.999) return read.range;
  if (w <= 0.001) return read.base;
  const out = read.base.clone();
  for (let i = 0; i < out.w.length; i++) {
    out.w[i] = read.base.w[i]! * (1 - w) + read.range.w[i]! * w;
  }
  return out;
}
